import React, { useState, useEffect } from 'react';
import { Typography, IconButton, Button, Dialog, DialogTitle, DialogContent, DialogActions, TextField } from '@mui/material';
import { IoMdCloseCircleOutline } from "react-icons/io";
import { MdEdit } from "react-icons/md";
import { FiTrash2 } from "react-icons/fi";
import SFCustomTable from '../../component/custom/SFCustomTable';
import ApiRequestService from '../../component/ApiRequestService';
import { NotificationManager } from 'react-notifications';
import DeleteDialog from '../DeleteDialog';
import { browserHistory } from 'react-router';
import { AppProperties } from '../../common/function';
import CustomToggle from '../../component/CustomToggle';
import { useLibrarySync, UnsyncedCategoriesDialog, ReadOnlyActionsCell, SyncedAtSourceCell, SyncLoadingCell, refuseEmptyRowSync, hasValidLibraryIds } from './LibrarySync';
import "./style.css";

const AssignmentMatrixLibraryTopic = () => {

    const appProperties = AppProperties();

    const [librarySubjectID, setLibrarySubjectID] = useState(null);
    const [librarySubjectName, setLibrarySubjectName] = useState("");
    const [libraryTopicID, setLibraryTopicID] = useState(null);
    const [libraryTopicName, setLibraryTopicName] = useState("");
    const [libraryTopic, setLibraryTopic] = useState(null);
    const [libraryMatrices, setLibraryMatrices] = useState([]);

    // True when the parent subject arrived from another university, which puts
    // every matrix under it — and adding a new one — out of reach here. The
    // topic answers this even with no matrices yet; the matrices answer it if
    // the topic read failed.
    const isReadOnly = !!libraryTopic?.is_synced || libraryMatrices.some(matrix => matrix.is_synced);

    const [showAddMatrixDialog, setShowAddMatrixDialog] = useState(false);
    const [editMatrix, setEditMatrix] = useState(null);
    const [selectedMatrix, setSelectedMatrix] = useState(null);
    const [showMatrixDeleteDialog, setShowMatrixDeleteDialog] = useState(false);

    const {
        syncAccess,
        syncLoading,
        syncStatusLoading,
        loadSyncStatus,
        reloadSyncStatus,
        getSyncInfo,
        toggleSync,
        unsyncedCategories,
        clearUnsyncedCategories,
    } = useLibrarySync({
        statusUrl: '/librarySyncMatricesStatus',
        toggleUrl: '/librarySyncEnableMatrix',
    });

    const onEnableMatrixSync = (row) => {
        if (refuseEmptyRowSync('matrix', row)) return;
        toggleSync(row.id, true, {
            aml_subject_id: librarySubjectID,
            aml_topic_id: libraryTopicID,
            aml_matrix_id: row.id,
        });
    }

    const onDisableMatrixSync = (row) => {
        toggleSync(row.id, false, {
            aml_subject_id: librarySubjectID,
            aml_topic_id: libraryTopicID,
            aml_matrix_id: row.id,
        });
    }


    // Dropped entirely without sync access - see AssignmentMatrixLibrary.js.
    const matrixColumns = [
        {
            name: <div className='sf-data-table-header'>Matrix ID</div>,
            selector: row => row.id,
            sortable: true,
            width: '200px',
        },
        {
            name: <div className='sf-data-table-header'>Matrix Name</div>,
            selector: row => row.name,
            sortable: true,
            grow: 2,
        },
        {
            name: <div className='sf-data-table-header'># of Assignments</div>,
            selector: row => row.total_quiz_count,
            sortable: true,
            grow: 2
        },
        {
            name: <div className='sf-data-table-header'>Created By</div>,
            selector: row => row.created_by,
            sortable: true,
            grow: 2
        },
        {
            id: 'sync',
            name: <div className='sf-data-table-header'>Sync</div>,
            sortable: false,
            width: '180px',
            cell: (row) => {
                let syncInfo = getSyncInfo(row.id);
                // Matrices of a subject owned elsewhere are read-only here.
                if (row.is_synced || syncInfo.is_synced) {
                    return <SyncedAtSourceCell
                        amlSubjectID={librarySubjectID}
                        sourcePath={`AssignmentMatrixLibraryMatrix?subject_id=${librarySubjectID}&subject_name=${encodeURIComponent(librarySubjectName)}&topic_id=${libraryTopicID}&topic_name=${encodeURIComponent(libraryTopicName)}&id=${row.id}&name=${encodeURIComponent(row.name)}`}
                    />;
                }
                if (syncStatusLoading) {
                    return <SyncLoadingCell />;
                }
                return (
                    <div style={{ marginTop: "8px" }}>
                        <CustomToggle
                            Enabled={syncInfo.enable_sync || false}
                            tristate={false}
                            onEnableChecked={() => onEnableMatrixSync(row)}
                            onDisableChecked={() => onDisableMatrixSync(row)}
                            onHandleMiddleredirect={() => {}}
                            syncStatus={syncInfo.sync_status_code || 0}
                            sourceRedirect={false}
                            type="switch"
                            id={"switch-libMatrix-" + row.id}
                        />
                    </div>
                );
            },
        },
        {
            name: <div className='sf-data-table-header'>Actions</div>,
            sortable: false,
            cell: (row) => {
                if (row.is_synced) {
                    return <ReadOnlyActionsCell />;
                }
                return (
                    <div>
                        <IconButton onClick={(e) => { e.stopPropagation(); setEditMatrix(row); setShowAddMatrixDialog(true); }}>
                            <MdEdit color='#3d5e9c' />
                        </IconButton>
                        <IconButton onClick={(e) => {
                            e.stopPropagation();
                            setShowMatrixDeleteDialog(true);
                            setSelectedMatrix(row);
                        }}>
                            <FiTrash2 color='#bb0000' />
                        </IconButton>
                    </div>
                )
            },
        },
    ].filter(column => syncAccess || column.id !== 'sync');

    useEffect(() => {
        const queryParams = new URLSearchParams(window.location.search);
        let subjectIdStr = queryParams.get("subject_id");
        let subjectNameStr = queryParams.get("subject_name");
        let topicIdStr = queryParams.get("topic_id");
        let topicNameStr = queryParams.get("topic_name");
        if (subjectNameStr) {
            setLibrarySubjectName(decodeURIComponent(subjectNameStr));
        }
        if (topicNameStr) {
            setLibraryTopicName(decodeURIComponent(topicNameStr));
        }
        if (subjectIdStr && topicIdStr) {
            let subjectId = parseInt(subjectIdStr, 10);
            let topicId = parseInt(topicIdStr, 10);
            if (subjectId && topicId) {
                setLibrarySubjectID(subjectId);
                setLibraryTopicID(topicId);
                getLibraryTopic(topicId, subjectId);
                getLibraryMatrices({
                    aml_subject_id: subjectId,
                    aml_topic_id: topicId,
                });
                loadSyncStatus({
                    aml_subject_id: subjectId,
                    aml_topic_id: topicId,
                });
            }
        }
    }, []);

    const getLibraryTopic = (topicID, subjectID) => {
        ApiRequestService({
            method: 'post',
            url: '/getAMLTopic',
            data: {
                id: topicID,
                aml_subject_id: subjectID,
            }
        }).then(res => {
            if (res && res.data && res.data.data) {
                setLibraryTopic(res.data.data);
                if (!libraryTopicName && res.data.data.name) {
                    setLibraryTopicName(res.data.data.name);
                }
            } else {
                setLibraryTopic(null);
            }
        })
    }

    const getLibraryMatrices = (payload) => {
        ApiRequestService({
            method: 'post',
            url: '/getAMLMatrices',
            data: payload,
        }).then(res => {
            if (res && res.data && res.data.data) {
                setLibraryMatrices(res.data.data);
            } else {
                setLibraryMatrices([]);
            }
        })
    }

    const handleAddMatrix = () => {
        setShowAddMatrixDialog(true);
    }

    const handleEditMatrix = (matrix) => {
        let baseFolder = appProperties.baseFolder;
        let url = `${baseFolder}AssignmentMatrixLibraryMatrix?subject_id=${librarySubjectID}&subject_name=${encodeURIComponent(librarySubjectName)}&topic_id=${libraryTopicID}&topic_name=${encodeURIComponent(libraryTopicName)}&id=${matrix.id}&name=${encodeURIComponent(matrix.name)}`;
        browserHistory.push(url);
    }

    const onAddMatrix = (newMatrix) => {
        setLibraryMatrices([...libraryMatrices, newMatrix]);
        setShowAddMatrixDialog(false);
        // No reload here: this navigates straight into the new matrix.
        handleEditMatrix(newMatrix);
    }

    const onEditMatrix = (updatedMatrix) => {
        let updated = libraryMatrices.map(m => m.id === updatedMatrix.id ? { ...m, name: updatedMatrix.name } : m);
        setLibraryMatrices(updated);
        setShowAddMatrixDialog(false);
        setEditMatrix(null);
        reloadSyncStatus();
    }

    const handleDeleteMatrix = () => {
        if (!hasValidLibraryIds(librarySubjectID, libraryTopicID, selectedMatrix?.id)) {
            NotificationManager.error("Could not delete the matrix: missing subject, topic or matrix id", "ERROR");
            return;
        }
        ApiRequestService({
            method: 'post',
            url: '/deleteAMLMatrix',
            data: {
                // Matrix ids are allocated per topic, so the full triple is the
                // key - the id alone matched no row and still reported success.
                aml_subject_id: librarySubjectID,
                aml_topic_id: libraryTopicID,
                id: selectedMatrix.id,
            }
        }).then(res => {
            // Reads the status, not the fact that the call came back - see
            // handleDeleteSubject in AssignmentMatrixLibrary.js.
            if (!res || !res.data || res.data.status !== 0) {
                NotificationManager.error(
                    (res && res.data && res.data.message) || "Could not delete the matrix", "ERROR");
                return;
            }

            setLibraryMatrices(current => current.filter(m => m.id !== selectedMatrix.id));
            setShowMatrixDeleteDialog(false);
            setSelectedMatrix(null);
            reloadSyncStatus();
            NotificationManager.success("Successfully deleted the matrix");
        }).catch(err => {
            console.error("Error deleting library matrix:", err);
            NotificationManager.error("Could not delete the matrix", "ERROR");
        })
    }

    return (
        <div className='assign-lib-cont'>
            <div className='assign-lib-breadcrumb'>
                <span
                    className='assign-lib-breadcrumb-link'
                    onClick={() => {
                        let baseFolder = appProperties.baseFolder;
                        browserHistory.push(`${baseFolder}AssignmentMatrixLibrary`);
                    }}
                >
                    Assignment Matrix Library
                </span>
                <span className='assign-lib-breadcrumb-separator'>/</span>
                <span
                    className='assign-lib-breadcrumb-link'
                    onClick={() => {
                        let baseFolder = appProperties.baseFolder;
                        browserHistory.push(`${baseFolder}AssignmentMatrixLibrarySubject?id=${librarySubjectID}&name=${encodeURIComponent(librarySubjectName)}`);
                    }}
                >
                    {librarySubjectName || "Subject"}
                </span>
                <span className='assign-lib-breadcrumb-separator'>/</span>
                <span className='assign-lib-breadcrumb-current'>
                    {libraryTopicName || "Matrices"}
                </span>
            </div>
            <Typography fontWeight={500} fontSize={24}>
                {libraryTopic ? `${libraryTopic.name} -` : null} Matrices
            </Typography>
            <div style={{ marginTop: '1rem' }}>
                <SFCustomTable
                    loading={syncLoading}
                    selectableRows={false}
                    columns={matrixColumns}
                    data={libraryMatrices}
                    rightContent={
                        <div>
                            {isReadOnly ? null :
                                <Button variant='contained' onClick={handleAddMatrix}>Add Matrix</Button>}
                        </div>
                    }
                    topPagination={false}
                    bottomPagination={true}
                    handleRowClick={(matrix) => {
                        handleEditMatrix(matrix);
                    }}
                    defaultRowsPerPage={20}
                    showSearch={true}
                    searchPlaceholder={"Search by matrix name"}
                    searchByFields={["name"]}
                />
            </div>

            <AddMatrixDialog
                open={showAddMatrixDialog}
                handleClose={() => { setShowAddMatrixDialog(false); setEditMatrix(null); }}
                subjectID={librarySubjectID}
                topicID={libraryTopicID}
                onAddMatrix={onAddMatrix}
                editMatrix={editMatrix}
                onEditMatrix={onEditMatrix}
            />

            <UnsyncedCategoriesDialog
                unsyncedCategories={unsyncedCategories}
                onClose={clearUnsyncedCategories}
            />

            <DeleteDialog
                showDialog={showMatrixDeleteDialog}
                title="Delete Matrix"
                description={
                    <div>Are you sure you want to delete "{selectedMatrix?.name}" matrix?</div>
                }
                onCancel={() => {
                    setShowMatrixDeleteDialog(false);
                }}
                onDelete={() => {
                    handleDeleteMatrix();
                }}
            />
        </div>
    );
}

const AddMatrixDialog = ({ open, handleClose, subjectID, topicID, onAddMatrix, editMatrix, onEditMatrix }) => {
    const [name, setName] = useState("");

    useEffect(() => {
        if (editMatrix) {
            setName(editMatrix.name || "");
        } else if (!open) {
            setName("");
        }
    }, [open, editMatrix]);

    const addMatrix = () => {
        ApiRequestService({
            method: 'post',
            url: '/addAMLMatrix',
            data: {
                name: name,
                aml_subject_id: subjectID,
                aml_topic_id: topicID,
            }
        }).then(res => {
            if (res && res.data && res.data.data) {
                onAddMatrix(res.data.data);
            }
        })
    }

    const editMatrixName = () => {
        let payload = {
            ...editMatrix,
            name: name,
        }
        ApiRequestService({
            method: 'post',
            url: '/updateAMLMatrix',
            data: payload,
        }).then(res => {
            if (res && res.data && res.data.data) {
                onEditMatrix(payload);
            }
        })
    }

    return (
        <Dialog
            open={open}
            maxWidth="sm"
            fullWidth
        >
            <DialogTitle>
                <Typography fontSize={18} fontWeight={500}>
                    {editMatrix ? "Edit Matrix" : "New Matrix"}
                </Typography>
            </DialogTitle>
            <IconButton
                aria-label="close"
                sx={{
                    position: 'absolute',
                    right: 8,
                    top: 8,
                }}
                onClick={handleClose}
            >
                <IoMdCloseCircleOutline fontSize={24} />
            </IconButton>
            <DialogContent>
                <TextField
                    placeholder='Enter matrix name'
                    sx={{ width: "100%" }}
                    value={name}
                    onChange={event => setName(event.target.value)}
                />
            </DialogContent>
            <DialogActions>
                <Button variant='outlined' onClick={handleClose}>
                    Close
                </Button>
                <Button
                    variant='contained'
                    onClick={() => {
                        if (editMatrix) {
                            editMatrixName();
                        } else {
                            addMatrix();
                        }
                    }}
                    disabled={name === ""}
                >
                    {editMatrix ? "Save" : "Create"}
                </Button>
            </DialogActions>
        </Dialog>
    );
}

export default AssignmentMatrixLibraryTopic;
